import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * GroupViewContainer
 *
 * Data resolver + state owner for the Group view. Fetches:
 *   1. The group itself + its direct top-level blocks
 *      (`GET /v1/collection/:id/details`).
 *   2. The 1st-level child collections (`GET /v1/collection/:id/children`).
 *
 * Owns the Group view's local UI state too: the inline "New collection"
 * create form and the "Filter collections…" search query. These mirror
 * the Collections listing toolbar so the two surfaces feel uniform.
 *
 * Renders <GroupView> when both queries resolve. Surfaces errors
 * inline rather than throwing so the user sees a recoverable
 * "Failed to load" instead of a blank screen.
 */
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { listCollectionChildren } from "@/api/query-api";
import { blockStore } from "@/data/block-store";
import { collectionStore } from "@/data/collection-store";
import { useAnnotationsForBlocks } from "@/data/use-annotations";
import { CollectionSettingsModal } from "@/components/collection-settings-modal/collection-settings-modal";
import { useGoBack } from "@/hooks/use-go-back";
import { useCollectionUrlBuilder, useUrlForCollectionId } from "@/lib/urls";
import { track } from "@/lib/analytics";
import { GroupView } from "./group-view";
const GROUP_DETAIL_PAGE_SIZE = 100;
export const GroupViewContainer = memo(({ groupId }) => {
    const navigate = useNavigate();
    const goBack = useGoBack();
    const buildCollectionUrl = useCollectionUrlBuilder();
    const buildUrlForCollectionId = useUrlForCollectionId();
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [createPending, setCreatePending] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    // Group is a layer-0 Collection — same block-index machinery covers
    // it. `useBlocks` reflects optimistic adds (block dragged into the
    // group's direct membership) instantly via TanStack DB live query.
    const blocksList = collectionStore.useBlocks(groupId);
    useEffect(() => {
        void collectionStore.loadBlocks(groupId, { limit: GROUP_DETAIL_PAGE_SIZE });
    }, [groupId]);
    const group = collectionStore.useFindById(groupId);
    const childrenQuery = useQuery({
        queryKey: ["app.groupChildren", groupId],
        queryFn: () => listCollectionChildren(groupId),
        staleTime: 30_000,
    });
    const handleBack = goBack;
    const handleOpenCollection = useCallback((collection) => {
        track({ name: "collection.opened", props: { source: "card" } });
        navigate(buildCollectionUrl(collection));
    }, [navigate, buildCollectionUrl]);
    const handleSettingsClose = useCallback(() => {
        setIsSettingsOpen(false);
        void collectionStore.refreshCollections();
    }, []);
    const handleToggleCreate = useCallback(() => {
        setIsCreating((prev) => {
            if (prev)
                setNewTitle("");
            return !prev;
        });
    }, []);
    const handleCancelCreate = useCallback(() => {
        setIsCreating(false);
        setNewTitle("");
    }, []);
    const handleSubmitCreate = useCallback(async (e) => {
        e.preventDefault();
        const trimmed = newTitle.trim();
        if (trimmed.length === 0 || createPending)
            return;
        setCreatePending(true);
        try {
            const id = await collectionStore.createChildCollection(groupId, {
                title: trimmed,
            });
            setIsCreating(false);
            setNewTitle("");
            navigate(buildUrlForCollectionId(id));
        }
        catch {
            showToast.error("Failed to create collection");
        }
        finally {
            setCreatePending(false);
        }
    }, [groupId, newTitle, createPending, navigate, buildUrlForCollectionId]);
    const handleClearSearchQuery = useCallback(() => {
        setSearchQuery("");
    }, []);
    const allChildCollections = childrenQuery.data ?? [];
    const visibleChildCollections = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (query.length === 0)
            return allChildCollections;
        return allChildCollections.filter((c) => (c.title ?? "Untitled").toLowerCase().includes(query));
    }, [allChildCollections, searchQuery]);
    // Warm the user-annotation collection for the group's blocks so each
    // mounted `BlockViewContainer` finds rows already in the local cache.
    const groupBlockIds = useMemo(() => blocksList.rows.map((r) => r.blockId), [blocksList.rows]);
    useAnnotationsForBlocks(groupBlockIds);
    // Synthesize envelopes for `<GroupView>` (which still expects the
    // legacy shape). Cards self-resolve via `useById` (Design B), so the
    // synth only carries id + sortKey for layout decisions.
    const blocks = useMemo(() => blocksList.rows.map((row) => {
        const cached = blockStore.get(row.blockId);
        if (cached)
            return cached;
        return {
            block: {
                id: row.blockId,
                type: "text",
                content: { text: "" },
                collectionId: "",
                createdBy: "",
                lineageId: "",
                title: "",
                summary: "",
                metadata: { channel: "viewer_client" },
                ingestorVersion: "0",
                processingStatus: "pending",
                capturedAt: row.sortKey,
                createdAt: row.sortKey,
                surfaceAt: row.sortKey,
            },
        };
    }), [blocksList.rows]);
    const isLoading = blocksList.isLoading || childrenQuery.isLoading;
    if (isLoading && blocksList.rows.length === 0) {
        return (_jsx("div", { className: "loading-spinner-container", children: _jsx(FaIcon, { icon: faSpinnerThird, className: "loading-spinner" }) }));
    }
    if (!group) {
        return _jsx("p", { className: "app-screen__status", children: "Failed to load group." });
    }
    return (_jsxs(_Fragment, { children: [_jsx(GroupView, { group: group, blocks: blocks, childCollections: visibleChildCollections, isLoading: isLoading, isCreating: isCreating, newTitle: newTitle, createPending: createPending, searchQuery: searchQuery, onBack: handleBack, onOpenSettings: () => setIsSettingsOpen(true), onOpenCollection: handleOpenCollection, onToggleCreate: handleToggleCreate, onChangeNewTitle: setNewTitle, onCancelCreate: handleCancelCreate, onSubmitCreate: handleSubmitCreate, onChangeSearchQuery: setSearchQuery, onClearSearchQuery: handleClearSearchQuery }), isSettingsOpen ? (_jsx(CollectionSettingsModal, { collection: group, onClose: handleSettingsClose })) : null] }));
});
